import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'sonner';

import { queryClient } from '@/lib/query-client';
import { env } from '@/lib/env';
import { RootErrorFallback } from '@/components/feedback/RootErrorFallback';
import { CookieConsentManager } from '@/features/cookie-consent/components/CookieConsentManager';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Composition root for cross-cutting providers. Keep this lean and ordered:
 * error boundary (outermost so it catches provider errors) → query client →
 * app tree, with the global toast portal alongside.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <ErrorBoundary FallbackComponent={RootErrorFallback}>
      <QueryClientProvider client={queryClient}>
        {children}
        <Toaster position="top-right" richColors closeButton />
        <CookieConsentManager />
        {env.VITE_ENABLE_QUERY_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
